<template>
    <div>
        <v-container class="pa-5" fluid>
            <ConfirmDialog
                :is-open="deleteOutdoorsCampaignDialog"
                :data="outdoorsCampaign"
                :action-loading="actionLoading.deleteOutdoorsCampaignLoading"
                @on-confirm="removeOutdoorsCampaign(outdoorsCampaign)"
                @on-abort="closeDeleteOutdoorsCampaignDialog"
            >
                <template #title>
                    Usuwanie kampanii
                </template>
                <template #content>
                    Czy na pewno chcesz usunąć kampanię?
                </template>
                <template #confirmButton>
                    Usuń
                </template>
            </ConfirmDialog>
            <v-row align="center" dense>
                <v-col cols="auto">
                    <page-title>Kampanie</page-title>
                </v-col>
            </v-row>

            <v-row align="center">
                <v-col cols="12" lg="4">
                    <v-btn
                        color="secondary"
                        medium
                        data-sentry-label="Dodaj kampanię"
                        :loading="actionLoading.createOutdoorsCampaignLoading"
                        @click="createOutdoorsCampaign()"
                    >
                        <v-icon left>
                            mdi-plus
                        </v-icon>
                        Dodaj kampanię
                    </v-btn>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-card>
                        <v-data-table
                            :headers="headers"
                            :items="outdoorsCampaigns"
                            :loading="outdoorsCampaignsLoading"
                            :footer-props="footerProps"
                            :page.sync="pagination.page"
                            :server-items-length="pagination.itemsLength"
                            :mobile-breakpoint="0"
                            name="outdoorsCampaignData"
                            @pagination="updatePagination"
                        >
                            <template #item="{item}">
                                <tr :class="{'isSelected': selectedCampaignId == item.id}">
                                    <td>
                                        {{ item.name }}
                                    </td>
                                    <td>
                                        {{ item.customer.name }}
                                    </td>
                                    <td>
                                        {{ formatDate(item.createdAt) }}
                                    </td>
                                    <td>
                                        <template v-if="item.startDateTime">
                                            {{ formatDate(item.startDateTime) }}
                                        </template>
                                        <template v-else>
                                            -
                                        </template>
                                    </td>
                                    <td>
                                        <template v-if="item.endDateTime">
                                            {{ formatDate(item.endDateTime) }}
                                        </template>
                                        <template v-else>
                                            -
                                        </template>
                                    </td>
                                    <td>
                                        <template v-if="item.createdBy.firstName && item.createdBy.lastName">
                                            {{ item.createdBy.firstName }} {{ item.createdBy.lastName }}
                                        </template>
                                        <template v-else>
                                            {{ item.createdBy.username }}
                                        </template>
                                    </td>
                                    <td class="text-center">
                                        <div class="d-inline-flex">
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        icon
                                                        data-sentry-label="Generuj raport"
                                                        v-on="on"
                                                        @click="generateReport(item)"
                                                    >
                                                        <v-icon color="info">
                                                            mdi-file-excel
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Generuj raport</span>
                                            </v-tooltip>
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        icon
                                                        data-sentry-label="Edytuj"
                                                        v-on="on"
                                                        @click="openOutdoorsCampaign(item.id)"
                                                    >
                                                        <v-icon color="teal">
                                                            mdi-pencil
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Edytuj</span>
                                            </v-tooltip>
                                            <v-tooltip top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        icon
                                                        data-sentry-label="Usuń"
                                                        v-on="on"
                                                        @click="showDeleteOutdoorsCampaign(item.id)"
                                                    >
                                                        <v-icon color="pink">
                                                            mdi-delete
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Usuń</span>
                                            </v-tooltip>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import pagination from '@/mixins/dataTablePagination'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import tooltipDelay from '@/mixins/tooltip'
import dateFormatter from '@/mixins/dateFormatter'
import filters from '@/mixins/dataTableFilters'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'OutdoorsCampaignList',
    components: {
        PageTitle,
        ConfirmDialog
    },
    mixins: [
        pagination,
        tooltipDelay,
        dateFormatter,
        filters,
        dataTableClassSetter
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.clearSelectedListItemId()
        }
        next()
    },
    data() {
        return {
            deleteOutdoorsCampaignDialog: false,
            actionLoading: {
                createOutdoorsCampaignLoading: false,
                openOutdoorsCampaignLoading: false,
                deleteOutdoorsCampaignLoading: false,
            },
            outdoorsCampaign: {},
            headers: [
                {
                    text: 'Nazwa',
                    value: 'name',
                    sortable: false
                },
                {
                    text: 'Klient',
                    value: 'customer',
                    sortable: false
                },
                {
                    text: 'Data utworzenia',
                    value: 'createdAt',
                    sortable: false
                },
                {
                    text: 'Data rozpoczęcia',
                    value: 'startDate',
                    sortable: false
                },
                {
                    text: 'Data zakończenia',
                    value: 'endDate',
                    sortable: false
                },
                {
                    text: 'Zgłaszający',
                    value: 'createdBy',
                    sortable: false
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        outdoorsCampaigns() {
            return this.$store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.outdoorsCampaigns
        },
        outdoorsCampaignsLoading() {
            return this.$store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.outdoorsCampaignsLoading
        },
        user() {
            return this.$store.state.user
        },
        selectedCampaignId() {
            return this.$store.state.common.selectedListItemId
        },
    },
    beforeMount() {
        if (!this.savedPagination.pagination) {
            this.resetPagination()
        }
    },
    mounted() {
        this.getOutdoorsCampaigns()
    },
    methods: {
        getOutdoorsCampaigns() {
            this.$store.dispatch('outdoors/outdoorsCampaigns/getOutdoorsCampaigns', {
                paginate: this.paginate(),
                query: ''
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        async getOutdoorsCampaign(outdoorsCampaignId) {
            return await this.$store.dispatch('outdoors/outdoorsCampaigns/getOutdoorsCampaign', outdoorsCampaignId)
                .then(response => {
                    this.outdoorsCampaign = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        createOutdoorsCampaign() {
            this.actionLoading.createOutdoorsCampaignLoading = true
            this.$router.push({ name: 'outdoorsCampaignsCampaignsOutdoorsCampaignCreate' })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.createOutdoorsCampaignLoading = false
                })
        },
        openOutdoorsCampaign(localizationId) {
            this.actionLoading.openOutdoorsCampaignLoading = true
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(localizationId)
            this.$store.dispatch('outdoors/outdoorsCampaigns/setIsCampaignCreated')
            this.$router.push({ name: 'outdoorsCampaignsCampaignsOutdoorsCampaignEdit', params: {id: localizationId} })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.openOutdoorsCampaignLoading = false
                })
        },
        showDeleteOutdoorsCampaign(outdoorsCampaignId) {
            this.getOutdoorsCampaign(outdoorsCampaignId)
                .then(() => {
                    this.deleteOutdoorsCampaignDialog = true
                })
        },
        removeOutdoorsCampaign() {
            this.actionLoading.deleteOutdoorsCampaignLoading = true
            this.$store.dispatch('outdoors/outdoorsCampaigns/deleteOutdoorsCampaign', this.outdoorsCampaign.id )
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.getOutdoorsCampaigns()
                    this.closeDeleteOutdoorsCampaignDialog()
                })
        },
        closeDeleteOutdoorsCampaignDialog() {
            this.actionLoading.deleteOutdoorsCampaignLoading = false
            this.deleteOutdoorsCampaignDialog = false
            setTimeout(() => {
                this.outdoorsCampaign = {}
            }, 500)
        },
        generateReport(item) {
            this.generateReportLoading = true
            this.$store.dispatch('outdoors/outdoorsCampaigns/generateReport', {
                campaignId: item.id,
                userEmail: this.user.userEmail
            })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.generateReportLoading = false
                })
        },
        updatePagination(pagination) {
            this.pagination = pagination
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && !this.savedPagination.pagination) {
                this.getOutdoorsCampaigns()
            }
            this.clearPaginationPending()
        }
    },
}
</script>

